import { toast } from '@/hooks/use-toast';
import { isSystemBotTokenConfigured } from '@/services/telegram/botTokenService';
import { ScheduledNotification } from './types';
import { getScheduledNotificationsFromStorage, saveScheduledNotificationsToStorage, generateUUID } from './storageUtils';
import { initializeScheduledNotificationCheck } from './notificationProcessor';

/**
 * Schedule a notification to be sent at a later time
 */
export const scheduleLocalNotification = async (
  message: string,
  scheduledTime: Date,
  procurementId?: string,
  recipientIds?: string[],
  buttons?: {text: string, action: string, data: string}[]
): Promise<{success: boolean, scheduledId?: string}> => {
  try {
    // Check if bot token is configured
    const isConfigured = await isSystemBotTokenConfigured();
    if (!isConfigured) {
      toast({
        title: 'Bot Token Missing',
        description: 'Please configure your Telegram bot token first',
        variant: 'destructive'
      });
      return { success: false };
    }
    
    const id = generateUUID();
    const newNotification: ScheduledNotification = {
      id,
      message,
      scheduled_time: scheduledTime.toISOString(),
      created_at: new Date().toISOString(),
      status: 'pending',
      procurement_id: procurementId,
      recipient_ids: recipientIds,
      buttons
    };
    
    const notifications = getScheduledNotificationsFromStorage();
    notifications.push(newNotification);
    saveScheduledNotificationsToStorage(notifications);
    
    toast({
      title: 'Notification Scheduled',
      description: `Message will be sent on ${new Date(scheduledTime).toLocaleString()}`,
    });
    
    // Start the notification check if it's not already running
    initializeScheduledNotificationCheck();
    
    return {
      success: true,
      scheduledId: id
    };
  } catch (error: any) {
    console.error('Failed to schedule notification:', error);
    toast({
      title: 'Failed to Schedule',
      description: error.message || 'An unexpected error occurred',
      variant: 'destructive'
    });
    return { success: false };
  }
};

/**
 * Cancel a scheduled notification
 */
export const cancelLocalScheduledNotification = (
  scheduledId: string
): {success: boolean} => {
  try {
    const notifications = getScheduledNotificationsFromStorage();
    const index = notifications.findIndex(n => n.id === scheduledId);
    
    if (index === -1) {
      toast({
        title: 'Notification Not Found',
        description: 'The specified notification does not exist',
        variant: 'destructive'
      });
      return { success: false };
    }
    
    notifications[index].status = 'cancelled';
    saveScheduledNotificationsToStorage(notifications);
    
    toast({
      title: 'Notification Cancelled',
      description: 'The scheduled notification has been cancelled',
    });
    
    return { success: true };
  } catch (error: any) {
    console.error('Failed to cancel notification:', error);
    toast({
      title: 'Failed to Cancel',
      description: error.message || 'An unexpected error occurred',
      variant: 'destructive'
    });
    return { success: false };
  }
};

/**
 * Get scheduled notifications
 */
export const getLocalScheduledNotifications = (
  procurementId?: string,
  status?: string
): ScheduledNotification[] => {
  try {
    const notifications = getScheduledNotificationsFromStorage();
    
    // Apply filters if provided
    let filtered = notifications;
    
    if (procurementId) {
      filtered = filtered.filter(n => n.procurement_id === procurementId);
    }
    
    if (status) {
      filtered = filtered.filter(n => n.status === status);
    }
    
    return filtered.sort((a, b) => 
      new Date(a.scheduled_time).getTime() - new Date(b.scheduled_time).getTime()
    );
  } catch (error: any) {
    console.error('Failed to get scheduled notifications:', error);
    return [];
  }
};
